import { useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { MessageContent } from './types';

interface UseChatStreamOptions {
  onToken: (text: string) => void;
  onDone: () => void;
  onError: (error: string) => void;
}

export function useChatStream({ onToken, onDone, onError }: UseChatStreamOptions) {
  const abortControllerRef = useRef<AbortController | null>(null);

  const sendMessage = useCallback(async (
    messages: { role: 'user' | 'assistant'; content: MessageContent }[],
    systemPrompt: string,
    entityId: string
  ) => {
    // Cancel any existing stream
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        onError('Not authenticated');
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/chat/briefing`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            messages: messages.map(m => ({ role: m.role, content: m.content })),
            system: systemPrompt,
            entity_id: entityId,
          }),
          signal: controller.signal,
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        onError(errorData.error || `Request failed (${response.status})`);
        return;
      }

      const reader = response.body?.getReader();
      if (!reader) {
        onError('No response stream');
        return;
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) {
          onDone();
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        let currentEvent = '';
        for (const line of lines) {
          if (line.startsWith('event: ')) {
            currentEvent = line.slice(7).trim();
          } else if (line.startsWith('data: ')) {
            const data = line.slice(6).trim();
            try {
              const parsed = JSON.parse(data);
              if (currentEvent === 'token' && parsed.text) {
                onToken(parsed.text);
              } else if (currentEvent === 'done') {
                onDone();
                return;
              } else if (currentEvent === 'error') {
                onError(parsed.error || 'Stream error');
                return;
              }
            } catch {
              // Skip unparseable data
            }
          }
        }
      }
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'AbortError') {
        // User cancelled — not an error
        onDone();
        return;
      }
      const message = err instanceof TypeError
        ? 'Unable to reach the chat service. Please check your connection and try again.'
        : err instanceof Error ? err.message : 'Connection failed';
      onError(message);
    } finally {
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null;
      }
    }
  }, [onToken, onDone, onError]);

  const cancel = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
  }, []);

  return { sendMessage, cancel };
}
